import { ReactNode, useContext, useMemo } from 'react';
import { Box, Typography, TypographyProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { WarningCircleIcon } from '@/UI/Icons';
import { Flex, FlexCenVer } from '@/UI/styles';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { CreditsContext } from '@/services/CreditsContext';

const Text = ({ children, variant }: { children: ReactNode; variant: TypographyProps['variant'] }) => (
  <Typography variant={variant} color={color.Yellow100}>
    {children}
  </Typography>
);

const Btn = ({
  onClick,
  children,
  variant,
}: {
  onClick: () => void;
  children: ReactNode;
  variant: TypographyProps['variant'];
}) => (
  <ButtonTextPlain onClick={onClick} variant={variant} color={color.Yellow100}>
    {children}
  </ButtonTextPlain>
);

const LowCreditsMsg = ({ isSingleLine = false }: { isSingleLine?: boolean }) => {
  const { t } = useTranslation();
  const { handleGetMoreCreditsClick, openUpgradeModal } = useContext(CreditsContext);
  const { isSubscriptionTypeIn, isAllowedForPermission } = useSubscriptionPermissions();

  const getTextComponent = useMemo(() => {
    const textVariant = isSingleLine ? 'body-sm-rg' : 'body-xs-rg';
    if (isSubscriptionTypeIn([SubscriptionType.Free])) {
      return (
        <>
          <Text variant={textVariant}>
            {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE)}
          </Text>
          &nbsp;
          <Btn onClick={openUpgradeModal} variant={textVariant}>
            {t(
              I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                .LOW_CREDITS_MESSAGE_PART_2_FREE,
            )}
          </Btn>
        </>
      );
    } else {
      if (isAllowedForPermission('credits', 'action')) {
        return (
          <>
            <Text variant={textVariant}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE)}
            </Text>
            &nbsp;
            <Btn onClick={handleGetMoreCreditsClick} variant={textVariant}>
              {t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                  .LOW_CREDITS_MESSAGE_PART_2_GET_MORE_CREDITS_CTA,
              )}
            </Btn>
            &nbsp;
            <Text variant={textVariant}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_OR)}
            </Text>
            &nbsp;
            <Btn onClick={openUpgradeModal} variant={textVariant}>
              {t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                  .LOW_CREDITS_MESSAGE_PART_3_UPGRADE_CTA,
              )}
            </Btn>
          </>
        );
      } else {
        // view permission goes here
        return (
          <Text variant={textVariant}>
            {isSingleLine
              ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE)
              : t(
                  I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                    .LOW_CREDITS_MESSAGE_SHORT,
                )}
            &nbsp;
            {t(
              I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                .LOW_CREDITS_MESSAGE_PART_2_MEMBER,
            )}
          </Text>
        );
      }
    }
  }, [isSingleLine, isSubscriptionTypeIn, t, openUpgradeModal, isAllowedForPermission, handleGetMoreCreditsClick]);

  if (isSingleLine) {
    return (
      <Flex sx={{ gap: 0.75, backgroundColor: color.Yellow16_T, py: 0.5, px: 0.75, borderRadius: 1, width: '100%' }}>
        <WarningCircleIcon color={color.Yellow100} />
        <FlexCenVer>{getTextComponent}</FlexCenVer>
      </Flex>
    );
  }

  return (
    <Flex
      sx={{
        gap: 0.75,
        backgroundColor: color.Yellow16_T,
        py: 0.5,
        px: 0.75,
        borderRadius: 1,
        height: 34,
        width: '100%',
      }}
    >
      <FlexCenVer>
        <WarningCircleIcon color={color.Yellow100} width={16} height={16} />
      </FlexCenVer>
      {/* Do not remove fontSize - hack to make multiple spans fit as single text */}
      <Box sx={{ fontSize: '10px' }}>{getTextComponent}</Box>
    </Flex>
  );
};

export default LowCreditsMsg;
